import React, { useState, useEffect } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { EntityIcon, SchemaForm, SchemaIcon, SystemPropRow, Modal } from './common';
import SharedWithRow from './SharedWithRow';
import { getEntityColor, getEntityBg } from '../constants/entityRegistry';
import { schemaLabel } from '../utils/schemaUtils';
import { getPillColor } from '../utils/pillColors';
import VersionHistoryModal from './VersionHistoryModal';
import AudioBlobPlayer from './AudioBlobPlayer';
import FilePreviewSection from './FilePreviewSection';
import { dateOf } from '../nucleus/format';
import './GraphVisualization.css';

export default function NodeDetailPanel({
  node,
  nodes = [],
  relationships = [],
  schemasMap,
  onClose,
  onUpdateNode,
  onDeleteNode,
  onAddRelationship,
  onNavigate,
  onSelectNode,
  onRatifyNode,
  onUnratifyNode,
  readOnly = false,
}) {
  const { encKeys } = useAuth();
  const [editForm, setEditForm] = useState({ title: '', value: '', schemaMetadata: null });
  const [isDirty, setIsDirty] = useState(false);
  const [fullSchemaData, setFullSchemaData] = useState(null);
  const [fullNodeData, setFullNodeData] = useState(null);
  const [activeTab, setActiveTab] = useState('content');
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [showConnectionsModal, setShowConnectionsModal] = useState(false);

  // Cached schema name from the upfront batch (no json_schema included)
  const cachedSchema = node?.schema_id && schemasMap ? schemasMap.get(node.schema_id) : null;
  const schemaName = schemaLabel(cachedSchema?.schema_name) || (fullSchemaData?.id === node?.schema_id ? schemaLabel(fullSchemaData?.schema_name) : null);
  // Full schema data (with json_schema) is loaded lazily when Schema tab is opened
  const schemaData = fullSchemaData?.id === node?.schema_id ? fullSchemaData : cachedSchema;

  // Lazy fetch full node data when a node is selected
  useEffect(() => {
    if (!node?.id) return;
    // If value_json is already present from batch load, skip
    if (node.value_json !== undefined && node.value_json !== null) {
      setFullNodeData(null);
      return;
    }
    const controller = new AbortController();
    setFullNodeData(null);
    (async () => {
      try {
        const data = await api.request(`/nodes/${node.id}`);
        if (!controller.signal.aborted) setFullNodeData(data);
      } catch {
        if (!controller.signal.aborted) setFullNodeData(null);
      }
    })();
    return () => controller.abort();
  }, [node?.id, node?.value_json]);

  useEffect(() => {
    if (node) {
      const contentVal = (fullNodeData?.value_json ?? node.value_json ?? {})?.content ?? '';
      setEditForm({
        id: node.id,
        title: node.title || node.label || '',
        value: contentVal,
      });
      setIsDirty(false);
      setFullSchemaData(prev => prev?.id === node.schema_id ? prev : null);
      setActiveTab('content');
    }
  }, [node, fullNodeData]);


  // Eagerly fetch full schema (with json_schema) so fields render on Content tab
  useEffect(() => {
    if (!node?.schema_id) return;
    if (fullSchemaData?.id === node.schema_id) return;
    const controller = new AbortController();
    (async () => {
      try {
        const s = await api.getSchema(`${node.schema_id}?include_json_schema=true`);
        if (!controller.signal.aborted) setFullSchemaData(s);
      } catch {
        if (!controller.signal.aborted) setFullSchemaData(null);
      }
    })();
    return () => controller.abort();
  }, [node?.schema_id]);

  if (!node) return null;

  const handleInputChange = (field, value) => {
    if (readOnly) return;
    setEditForm(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleSave = () => {
    if (onUpdateNode && !readOnly) {
      // If SchemaForm was used, schemaMetadata has all fields; otherwise fall back to textarea value
      const valueJson = editForm.schemaMetadata
        ? { ...(node.value_json || {}), ...editForm.schemaMetadata }
        : { ...(node.value_json || {}), content: editForm.value };
      const updateData = {
        id: editForm.id,
        title: editForm.title,
        value_json: valueJson,
        schema_id: node.schema_id || undefined,
      };
      onUpdateNode(updateData);
      setIsDirty(false);
    }
  };

  const nodeRels = relationships.filter(rel => {
    const src = rel.source?.id || rel.source || rel.from_node_id;
    const tgt = rel.target?.id || rel.target || rel.to_node_id;
    return src === node.id || tgt === node.id;
  });
  const relTypes = [...new Set(nodeRels.map(r => r.relationship_type || 'related'))];

  const tabs = [
    { key: 'content', label: 'Content' },
  ];

  // Node-level metadata header (App, Ownership, Relations, Tags, dates, and the
  // Schema link). These describe the node itself — not its schema — so they must
  // render for every node, schema-defined or not. The UUID is added alongside as
  // a system field. Shared between the schema'd path (SchemaForm's systemSlot)
  // and the schemaless path (rendered directly below), so both look identical.
  const metaSlot = (
    <>
      {node.schema_id && schemaName && (
        <div className="prop-row" data-qh="prop-schema">
          <div className="prop-key" title="Schema">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
            <span className="prop-key__name">Schema</span>
          </div>
          <div className="prop-value">
            <button
              type="button"
              className="btn prop-link-value"
              onClick={() => onNavigate?.(`/graph?tab=schemas&schema_id=${node.schema_id}`)}
              title="Click to view schema details"
            >
              <span className="prop-pill" style={{ background: getPillColor(schemaName).bg, color: getPillColor(schemaName).color }}>
                {cachedSchema?.schema_metadata?.icon && <SchemaIcon iconName={cachedSchema.schema_metadata.icon} size={12} />}
                {schemaName}
              </span>
            </button>
          </div>
        </div>
      )}
      <div className="prop-row" data-qh="prop-app">
        <div className="prop-key" title="App">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>
          <span className="prop-key__name">App</span>
        </div>
        <div className="prop-value">
          {node.created_by_app_name
            ? <span className="prop-pill" style={{ background: getPillColor(node.created_by_app_name).bg, color: getPillColor(node.created_by_app_name).color }}>{node.created_by_app_name}</span>
            : <span className="prop-empty">Empty</span>}
        </div>
      </div>
      <div className="prop-row" data-qh="prop-owner">
        <div className="prop-key" title="Ownership">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="3.5"/><path d="M5 20c0-3.5 3-6 7-6s7 2.5 7 6"/></svg>
          <span className="prop-key__name">Ownership</span>
        </div>
        <div className="prop-value">
          {node.owner?.username ? (
            <span className="prop-pill prop-pill--owner">{node.owner.username}</span>
          ) : readOnly ? (
            <span className="prop-empty">Empty</span>
          ) : (
            <span className="prop-pill prop-pill--self">You</span>
          )}
        </div>
      </div>
      <SharedWithRow node={node} schema={schemaData} onManageRealms={() => onNavigate?.('/privacy-realms')} />
      <div className="prop-row prop-row--tags" data-qh="prop-relations">
        <div className="prop-key" title="Relations">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="5" cy="5" r="2.4"/><circle cx="19" cy="19" r="2.4"/><circle cx="19" cy="5" r="2.4"/><path d="M7.4 5H17M5 7.4V16a3 3 0 0 0 3 3h8.6"/></svg>
          <span className="prop-key__name">Relations</span>
        </div>
        <div className="prop-value prop-tags">
          {relTypes.length > 0 ? (
            <button type="button" className="btn prop-link-value" onClick={() => setShowConnectionsModal(true)} title="View connections">
              {relTypes.slice(0, 3).map(t => (
                <span key={t} className="prop-pill" style={{ background: getPillColor(t).bg, color: getPillColor(t).color }}>{t}</span>
              ))}
              {relTypes.length > 3 && <span className="prop-rel-more">+{relTypes.length - 3}</span>}
            </button>
          ) : <span className="prop-empty">Empty</span>}
        </div>
      </div>
      {dateOf(node.created_at) && (
        <div className="prop-row" data-qh="prop-created">
          <div className="prop-key" title="Created">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 9.5h18M8 3v4M16 3v4"/></svg>
            <span className="prop-key__name">Created</span>
          </div>
          <div className="prop-value"><span className="prop-meta-date">{dateOf(node.created_at)}</span></div>
        </div>
      )}
      {dateOf(node.updated_at) && (
        <div className="prop-row" data-qh="prop-updated">
          <div className="prop-key" title="Last updated">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7.5V12l3 2"/></svg>
            <span className="prop-key__name">Last updated</span>
          </div>
          <div className="prop-value"><span className="prop-meta-date">{dateOf(node.updated_at)}</span></div>
        </div>
      )}
      {node.stratum && (
        <div className="prop-row" data-qh="prop-stratum">
          <div className="prop-key" title="Stratum">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
            <span className="prop-key__name">Stratum</span>
          </div>
          <div className="prop-value">
            <span className={`prop-pill prop-pill--stratum-${node.stratum}`}>{node.stratum}</span>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="node-detail-panel">
      <div className="panel-header">
        <button className="btn close-btn" onClick={onClose} title="Close panel" aria-label="Close panel" data-qh="detail-close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
        <div className="panel-header__icon" style={{
          color: getEntityColor('node'),
          backgroundColor: getEntityBg('node'),
        }}>
          {cachedSchema?.schema_metadata?.icon
            ? <SchemaIcon iconName={cachedSchema.schema_metadata.icon} size={18} />
            : <EntityIcon type="node" size="lg" />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <input
            type="text"
            value={editForm.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            placeholder="Node Title"
            className="detail-title-input"
            data-qh="detail-title"
            readOnly={readOnly}
            disabled={readOnly}
          />
        </div>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              className={`btn panel-icon-btn ${isDirty ? 'panel-icon-btn--dirty' : ''}`}
              data-qh="detail-menu"
              title="More actions"
              aria-label="More actions"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="19" cy="12" r="1.6"/></svg>
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content className="panel-actions-dropdown" sideOffset={4} align="end">
              {!readOnly && (
                <DropdownMenu.Item
                  className="panel-action-item"
                  data-qh="detail-save"
                  disabled={!isDirty}
                  onSelect={() => { if (isDirty) handleSave(); }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><path d="M17 21v-8H7v8M7 3v5h8"/></svg>
                  <span>{isDirty ? 'Save changes' : 'No changes'}</span>
                </DropdownMenu.Item>
              )}
              <DropdownMenu.Item
                className="panel-action-item"
                data-qh="detail-connections"
                onSelect={() => setShowConnectionsModal(true)}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="6" r="2.5"/><circle cx="18" cy="18" r="2.5"/><circle cx="18" cy="6" r="2.5"/><path d="M8.5 6H15M6 8.5V15a2 2 0 0 0 2 2h7.5"/></svg>
                <span>Connections</span>
              </DropdownMenu.Item>
              <DropdownMenu.Item
                className="panel-action-item"
                data-qh="detail-versions"
                onSelect={() => setShowVersionModal(true)}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
                <span>Versions</span>
              </DropdownMenu.Item>
              <DropdownMenu.Item
                className="panel-action-item"
                data-qh="detail-json"
                onSelect={() => setShowJsonModal(true)}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M16 18l6-6-6-6M8 6l-6 6 6 6"/></svg>
                <span>View JSON</span>
              </DropdownMenu.Item>
              {onRatifyNode && node.stratum !== 'declared' && (
                <DropdownMenu.Item
                  className="panel-action-item"
                  data-qh="detail-ratify"
                  onSelect={() => onRatifyNode(node)}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>
                  <span>Declare as identity</span>
                </DropdownMenu.Item>
              )}
              {onUnratifyNode && node.stratum === 'declared' && (
                <DropdownMenu.Item
                  className="panel-action-item"
                  data-qh="detail-unratify"
                  onSelect={() => onUnratifyNode(node)}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M15 9l-6 6M9 9l6 6"/></svg>
                  <span>Remove declaration</span>
                </DropdownMenu.Item>
              )}
              {onDeleteNode && (
                <DropdownMenu.Item
                  className="panel-action-item panel-action-item--danger"
                  data-qh="detail-delete"
                  onSelect={() => onDeleteNode(node)}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14"/></svg>
                  <span>Move to trash</span>
                </DropdownMenu.Item>
              )}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>

      {/* Tabs — only shown when there's more than one (e.g. synapse Prototype) */}
      {tabs.length > 1 && (
        <div className="detail-tabs">
          {tabs.map(tab => (
            <button
              key={tab.key}
              className={`btn detail-tab ${activeTab === tab.key ? 'detail-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      <div className="panel-content">
        {/* Content Tab */}
        {activeTab === 'content' && (
          <>
            {/* Audio player for nodes with audio_raw_file_node_id (e.g. VideoCall recordings) */}
            {(fullNodeData?.value_json ?? node.value_json)?.audio_raw_file_node_id && (
              <div className="detail-section">
                <AudioBlobPlayer rawFileNodeId={(fullNodeData?.value_json ?? node.value_json).audio_raw_file_node_id} />
              </div>
            )}

            {/* File preview for nodes with Azure blob content (RawFile + child schemas) */}
            {!!(fullNodeData?.value_json ?? node.value_json)?.blob_key && (
              <div className="detail-section" data-qh="detail-file">
                <FilePreviewSection node={fullNodeData ?? node} />
              </div>
            )}

            {/* Content / Schema Form */}
            <div className="detail-section detail-section--grow">
              {schemaData?.json_schema?.properties ? (
                <SchemaForm
                  schema={schemaData.json_schema}
                  values={editForm.schemaMetadata || node.value_json || {}}
                  onChange={(key, val) => {
                    if (readOnly) return;
                    setEditForm(prev => ({
                      ...prev,
                      schemaMetadata: { ...(prev.schemaMetadata || node.value_json || {}), [key]: val }
                    }));
                    setIsDirty(true);
                  }}
                  mode={readOnly ? 'preview' : 'edit'}
                  layout="properties"
                  systemFields={[{ label: 'ID', value: node.id }]}
                  systemSlot={metaSlot}
                  encryptedFields={schemaData.encrypted_fields || []}
                  encKeys={encKeys}
                />
              ) : (
                <>
                  {/* Schemaless node — render the same node-level metadata header
                      (UUID, App, dates, tags…) the schema'd path gets, then the
                      free-text content below it. */}
                  <div className="schema-props">
                    <div className="schema-props__list">
                      <div className="schema-props__system">
                        <span style={{ display: 'contents' }} data-qh="prop-id">
                          <SystemPropRow field={{ label: 'ID', value: node.id }} />
                        </span>
                        {metaSlot}
                      </div>
                    </div>
                  </div>
                  <textarea
                    value={editForm.value}
                    onChange={(e) => handleInputChange('value', e.target.value)}
                    placeholder="Node content..."
                    rows={3}
                    className="detail-textarea"
                    data-qh="detail-content"
                    readOnly={readOnly}
                    disabled={readOnly}
                  />
                </>
              )}
            </div>
          </>
        )}

        {/* Connections Tab */}
        {showConnectionsModal && (
          <Modal isOpen onClose={() => setShowConnectionsModal(false)} title="Connections" size="md">
            <div className="detail-section">
              <span className="section-title"><EntityIcon type="relationship" /> Relationships ({nodeRels.length})</span>
              <div className="relationships-list">
                {nodeRels.map((rel, idx) => {
                  const src = rel.source?.id || rel.source || rel.from_node_id;
                  const tgt = rel.target?.id || rel.target || rel.to_node_id;
                  const isSource = src === node.id;
                  const otherNodeId = isSource ? tgt : src;
                  const otherNode = nodes.find(n => n.id === otherNodeId);
                  const embeddedNode = isSource ? rel.to_node : rel.from_node;
                  const otherTitle = otherNode?.title || otherNode?.name || embeddedNode?.title || otherNodeId;
                  return (
                    <div key={idx} className="relationship-item" style={{ justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                        <span className="rel-type">{rel.type || rel.relationship_type || 'RELATED'}</span>
                        <a
                          href={`/graph?tab=database&node=${otherNodeId}`}
                          className="rel-target"
                          style={{ cursor: 'pointer', color: 'var(--paradigm-moss, #5a7a5a)', textDecoration: 'underline' }}
                          onClick={(e) => {
                            e.preventDefault();
                            const targetNode = nodes.find(n => n.id === otherNodeId);
                            if (onSelectNode) {
                              // Pass found node or a stub with just the id —
                              // GraphView will handle fetching full data
                              onSelectNode(targetNode || { id: otherNodeId });
                            }
                          }}
                        >
                          {isSource ? '\u2192' : '\u2190'} {otherTitle}
                        </a>
                      </div>
                    </div>
                  );
                })}
                {nodeRels.length === 0 && (
                  <div style={{ padding: '12px', color: 'var(--text-muted)', fontSize: '12px', textAlign: 'center' }}>
                    No relationships
                  </div>
                )}
                {!readOnly && (
                  <div style={{ padding: '6px', borderTop: '1px solid var(--divider)', textAlign: 'center' }}>
                    <button className="btn"
                      style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: '11px', fontWeight: '500', cursor: 'pointer' }}
                      onClick={() => onAddRelationship?.(node)}
                    >+ Add Relationship</button>
                  </div>
                )}
              </div>
            </div>

            {/* Composed From */}
            {node.value_json?.composed_from?.length > 0 && (
              <div className="detail-section">
                <span className="section-title" style={{ color: 'var(--warning)' }}>Merged From</span>
                <div className="detail-info-box" style={{ background: 'var(--warning-light)', border: '1px solid var(--warning)' }}>
                  <p style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '8px', marginTop: 0, lineHeight: '1.4' }}>
                    {node.value_json.merge_reason || 'This node was created by merging multiple nodes.'}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {(node.value_json.composed_from_titles || node.value_json.composed_from).map((item, idx) => {
                      const title = node.value_json.composed_from_titles ? item : `Node ${idx + 1}`;
                      return (
                        <div key={idx} style={{ padding: '4px 8px', background: 'var(--bg-secondary)', borderRadius: '4px', fontSize: '12px', fontWeight: '500', color: 'var(--text-primary)' }}>
                          {title}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Merged Into */}
            {node.value_json?.merged_into && (
              <div className="detail-section">
                <span className="section-title" style={{ color: 'var(--danger)' }}>Archived (Merged)</span>
                <div className="detail-info-box" style={{ background: 'var(--danger-light)', border: '1px solid var(--danger)' }}>
                  <p style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '6px', marginTop: 0 }}>
                    This node has been merged into another node.
                  </p>
                  <div style={{ padding: '6px 8px', background: 'var(--bg-secondary)', borderRadius: '4px' }}>
                    <div style={{ fontWeight: '500', color: 'var(--text-primary)', fontSize: '12px' }}>
                      {node.value_json.merged_into_title || 'Unknown'}
                    </div>
                    <div style={{ fontSize: '9px', color: 'var(--text-muted)', fontFamily: 'monospace', marginTop: '2px' }}>
                      {node.value_json.merged_into}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Modal>
        )}


      </div>
      {showVersionModal && (
        <VersionHistoryModal
          node={node}
          schema={schemaData?.json_schema}
          encryptedFields={schemaData?.encrypted_fields || []}
          encKeys={encKeys}
          onUpdateNode={onUpdateNode}
          initialView="versions"
          onClose={() => setShowVersionModal(false)}
        />
      )}
      {showJsonModal && (
        <Modal isOpen onClose={() => setShowJsonModal(false)} title="JSON" size="md">
          <pre className="node-json-modal">
            {JSON.stringify(editForm.schemaMetadata || node.value_json || {}, null, 2)}
          </pre>
        </Modal>
      )}
    </div>
  );
}
